import { PathLayer } from "@deck.gl/layers";
import { PathLayerProps } from "@deck.gl/layers";
import { useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";
import { ObstacleEdit } from "@nens/threedi/openapi-client";

import { appDispatch } from "../../App";
import { useErrorIfModelNotUpToDate } from "../../hooks/queries";
import { getExistingLeveeEdits } from "../../state/existingEvents";
import { setSelected } from "../../state/existingLeveeEditsMenu";
import { guiShouldAllowSelectingAssetsOrPoints } from "../../state/guiStateHelper";
import {
  getIsReadyToFinalizeConfirm,
  getPoints,
  getStatus,
  removeLastPointAction,
  setPointsAction,
  submitLeveeEdit,
} from "../../state/leveeEdit";
import { getThreediModel } from "../../state/simulation";

import { STATIC_COLORS } from "./DeckGlConsts";
import { DrawLine } from "./helpers/DrawLine";

// Line / Levee edit tool. Shown when the levee edit edit tool is open; what it does depends
// on Redux state.

const useLeveeEditTool = () => {
  const isSelectable = useSelector(guiShouldAllowSelectingAssetsOrPoints);
  const threediModel = useSelector(getThreediModel);
  const errorMessage = useErrorIfModelNotUpToDate("Failed to add levee.", threediModel);
  const editStatus = useSelector(getStatus);
  const points = useSelector(getPoints);
  const isReadyToFinalizeConfirm = useSelector(getIsReadyToFinalizeConfirm);

  const setCallback = (points: XYLocation[]) => appDispatch(setPointsAction(points));

  const removeLastPointCallback = () => {
    appDispatch(removeLastPointAction());
  };

  const selectLevee = (levee: ObstacleEdit) => {
    if (levee && levee.uid && isSelectable) appDispatch(setSelected(levee.uid));
  };

  const existingLeveeEdits = useSelector(getExistingLeveeEdits);

  const defaultPathLayerProps = {
    widthMaxPixels: 15,
    positionFormat: `XY`,
    _pathType: "open",
    opacity: 1,
    capRounded: true,
    jointRounded: true,
    getPath: (d: XYLocation) => d.flat().flat(),
    visible: editStatus === "DRAWING_MODE",
    getWidth: 0.5,
    widthMinPixels: 1,
    pickable: false,
    getColor: STATIC_COLORS.LEVEE_EDIT_NOT_ALLOWED,
  };

  return [
    ...DrawLine({
      id: "LEVEE",
      setCallback,
      confirmDrawingCallback: () => appDispatch(submitLeveeEdit(errorMessage)),
      points: points || undefined,
      isReadyToFinalizeConfirm: isReadyToFinalizeConfirm,
      removeLastPointCallback: removeLastPointCallback,
      defaultPathLayerProps: defaultPathLayerProps as unknown as PathLayerProps,
    }),
    new PathLayer({
      ...defaultPathLayerProps,
      data: existingLeveeEdits,
      getPath: (d) => d.linestring.coordinates.flat().flat(),
      id: "existing-levee-lines-layer",
      visible: true,
      pickable: true,
      onClick: (info) => selectLevee(info.object),
    } as PathLayerProps<ObstacleEdit>),
  ];
};

export default useLeveeEditTool;
